import { Reducer } from 'redux';
import { IAction } from '../types';
import type { RoomAudienceItemV2, roomPersonInfoListJson } from '@/views/Voice/voiceMain/myRoom/userListV2/service';
import {
  applyAudienceV2Changes,
  createAudienceV2RoomState,
  getRoomState,
  mergeAndSortAudienceV2Items,
  normalizeAudienceV2Items,
  removeUserFromOtherAudienceRooms,
  setRoomState
} from './audienceV2Helper';

export { getAudienceV2RoomKey, normalizeAudienceV2Item } from './audienceV2Helper';

export type AudienceV2FromType = 0 | 3;
export type AudienceV2RefreshReason = 'heart' | 'sequence' | 'manual';
export type AudienceV2ChangeEventType = 'ENTER' | 'OFFLINE' | 'REMOVE' | 'UPDATE';

export interface AudienceV2LocalItem {
  userId: number;
  index: number;
  online: boolean;
  onlineDeviceTypes?: string[];
  userInfo: roomPersonInfoListJson;
}

export interface AudienceV2RoomState {
  roomId: number;
  fromType: AudienceV2FromType;
  loading: boolean;
  hasMore: boolean;
  nextOffset?: number | null;
  loaded: boolean;
  /** 子房间列表是否展开；展开时才接收 IM 增量维护本地态 */
  expanded?: boolean;
  sequence: number;
  needRefresh: boolean;
  refreshReason?: AudienceV2RefreshReason;
  items: AudienceV2LocalItem[];
  online?: boolean;
}

export interface AudienceV2State {
  rooms: Record<string, AudienceV2RoomState>;
}

export interface AudienceV2RoomPayload {
  roomId: number;
  fromType: AudienceV2FromType;
}

export interface SetAudienceV2RoomLoadingPayload extends AudienceV2RoomPayload {
  loading: boolean;
}

export interface AudienceV2RoomPagePayload extends AudienceV2RoomPayload {
  items: Array<RoomAudienceItemV2 | AudienceV2LocalItem>;
  hasMore: boolean;
  nextOffset?: number | null;
  sequence?: number;
  online?: boolean;
}

export interface AudienceV2ChangeItem {
  eventType: AudienceV2ChangeEventType;
  userId: number;
  index?: number;
  online?: boolean;
  onlineDeviceTypes?: string[];
  userInfo?: roomPersonInfoListJson;
}

export interface AudienceV2ChangePayload extends AudienceV2RoomPayload {
  sequence: number;
  changes: AudienceV2ChangeItem[];
}

export interface SetAudienceV2RoomExpandedPayload extends AudienceV2RoomPayload {
  expanded: boolean;
}

export interface AudienceV2RefreshPayload extends AudienceV2RoomPayload {
  reason?: AudienceV2RefreshReason;
  needRefresh?: boolean;
  online?: boolean;
}

export interface AudienceV2ClearRefreshPayload extends AudienceV2RoomPayload {
  online?: boolean;
}

export const audienceV2: AudienceV2State = {
  rooms: {}
};

export const setAudienceV2RoomLoading: (
  payload: SetAudienceV2RoomLoadingPayload
) => IAction<SetAudienceV2RoomLoadingPayload> = payload => ({
  type: 'setAudienceV2RoomLoading',
  payload
});

export const replaceAudienceV2RoomPage: (
  payload: AudienceV2RoomPagePayload
) => IAction<AudienceV2RoomPagePayload> = payload => ({
  type: 'replaceAudienceV2RoomPage',
  payload
});

export const appendAudienceV2RoomPage: (
  payload: AudienceV2RoomPagePayload
) => IAction<AudienceV2RoomPagePayload> = payload => ({
  type: 'appendAudienceV2RoomPage',
  payload
});

export const receiveAudienceV2Change: (
  payload: AudienceV2ChangePayload
) => IAction<AudienceV2ChangePayload> = payload => ({
  type: 'receiveAudienceV2Change',
  payload
});

export const markAudienceV2Refresh: (
  payload: AudienceV2RefreshPayload
) => IAction<AudienceV2RefreshPayload> = payload => ({
  type: 'markAudienceV2Refresh',
  payload
});

export const clearAudienceV2Refresh: (
  payload: AudienceV2ClearRefreshPayload
) => IAction<AudienceV2ClearRefreshPayload> = payload => ({
  type: 'clearAudienceV2Refresh',
  payload
});

export const resetAudienceV2Room: (payload: AudienceV2RoomPayload) => IAction<AudienceV2RoomPayload> = payload => ({
  type: 'resetAudienceV2Room',
  payload
});

export const setAudienceV2RoomExpanded: (
  payload: SetAudienceV2RoomExpandedPayload
) => IAction<SetAudienceV2RoomExpandedPayload> = payload => ({
  type: 'setAudienceV2RoomExpanded',
  payload
});

const audienceV2Reducer: Reducer<AudienceV2State, IAction<any>> = (state = audienceV2, action: IAction<any>) => {
  const { type, payload } = action;

  switch (type) {
    case 'setAudienceV2RoomLoading': {
      const room = getRoomState(state, payload);
      return setRoomState(state, {
        ...room,
        loading: payload.loading
      });
    }
    case 'replaceAudienceV2RoomPage': {
      const room = getRoomState(state, payload);
      return setRoomState(state, {
        ...room,
        loading: false,
        hasMore: payload.hasMore,
        nextOffset: payload.nextOffset ?? null,
        loaded: true,
        sequence: payload.sequence ?? 0,
        needRefresh: false,
        refreshReason: undefined,
        online: payload.online ?? room.online,
        items: mergeAndSortAudienceV2Items(normalizeAudienceV2Items(payload.items))
      });
    }
    case 'appendAudienceV2RoomPage': {
      const room = getRoomState(state, payload);
      return setRoomState(state, {
        ...room,
        loading: false,
        hasMore: payload.hasMore,
        nextOffset: payload.nextOffset ?? null,
        loaded: true,
        online: payload.online ?? room.online,
        items: mergeAndSortAudienceV2Items([...room.items, ...normalizeAudienceV2Items(payload.items)])
      });
    }
    case 'receiveAudienceV2Change': {
      const room = getRoomState(state, payload);
      const sequence = Number(payload.sequence);
      if (!Number.isFinite(sequence)) return state;

      // 跳号不强行套用增量，标记刷新后由列表组件回源第一页，避免本地顺序继续漂移。
      if (room.sequence !== 0 && sequence !== room.sequence + 1) {
        return setRoomState(state, {
          ...room,
          needRefresh: true,
          refreshReason: 'sequence'
        });
      }

      let nextState = setRoomState(state, {
        ...room,
        sequence,
        items: applyAudienceV2Changes(room, payload.changes)
      });

      (payload.changes || []).forEach((change: AudienceV2ChangeItem) => {
        if (change.eventType !== 'ENTER') return;
        const userId = Number(change.userId);
        if (!Number.isFinite(userId)) return;
        nextState = removeUserFromOtherAudienceRooms(nextState, payload, userId);
      });

      return nextState;
    }
    case 'markAudienceV2Refresh': {
      const room = getRoomState(state, payload);
      const shouldMarkRefresh = payload.needRefresh !== false;
      const nextNeedRefresh = shouldMarkRefresh ? true : room.needRefresh;
      const nextRefreshReason = shouldMarkRefresh ? payload.reason || 'manual' : room.refreshReason;
      const nextOnline = payload.online ?? room.online;

      if (
        nextNeedRefresh === room.needRefresh &&
        nextRefreshReason === room.refreshReason &&
        nextOnline === room.online
      ) {
        return state;
      }

      return setRoomState(state, {
        ...room,
        needRefresh: nextNeedRefresh,
        refreshReason: nextRefreshReason,
        online: nextOnline
      });
    }
    case 'clearAudienceV2Refresh': {
      const room = getRoomState(state, payload);
      return setRoomState(state, {
        ...room,
        needRefresh: false,
        refreshReason: undefined,
        online: payload.online ?? room.online
      });
    }
    case 'resetAudienceV2Room':
      return setRoomState(state, createAudienceV2RoomState(payload));
    case 'setAudienceV2RoomExpanded': {
      const room = getRoomState(state, payload);
      if (room.expanded === payload.expanded) return state;
      return setRoomState(state, {
        ...room,
        expanded: payload.expanded
      });
    }
    default:
      return state;
  }
};

export default audienceV2Reducer;
